// src/pages/Login.js
import React, { useState } from "react";
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  IconButton,
  InputAdornment,
  Alert,
  Snackbar,
  CircularProgress,
} from "@mui/material";
import { Visibility, VisibilityOff, Email, Lock } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { postData } from "../services/request";
import logo from "../assets/logo/logo3.png";

const labelSx = {
  display: "block",
  color: "#52057b",
  fontWeight: 600,
  fontSize: "0.9rem",
  mb: 1,
};

const inputSx = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    backgroundColor: "#fff",
    boxShadow: "0px 2px 8px rgba(0,0,0,0.05)",
    transition: "all 0.3s ease",
    "&:hover": {
      boxShadow: "0px 4px 12px rgba(82, 5, 123, 0.1)",
    },
    "&.Mui-focused": {
      boxShadow: "0px 4px 15px rgba(82, 5, 123, 0.2)",
    },
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#e0e0e0",
  },
  "& .MuiInputBase-input": {
    py: 1.8,
  },
};

const Login = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const handleLogin = async () => {
    if (!email || !password) {
      setSnackbar({
        open: true,
        message: "Please enter email and password",
        severity: "warning",
      });
      return;
    }

    setLoading(true);
    try {
      const deviceId = localStorage.getItem("deviceId") || "pc";
      const payload = {
        email: email,
        password: password,
        deviceId: deviceId,
      };

      const response = await postData("/api/auth/signin", payload);

      if (response?.status === true || response?.success === true) {
        const userData = response?.data || {};
        const userNin = response?.nin || {};
        const savedUserData = {
          ...userData,
          spentTotal: response?.spentTotal ?? userData?.spentTotal ?? 0,
          receivedTotal:
            response?.receivedTotal ?? userData?.receivedTotal ?? 0,
          country: response?.country ?? userData?.country ?? null,
          notifications: Array.isArray(response?.notifications)
            ? response.notifications
            : userData?.notifications || [],
        };

        if (deviceId) {
          localStorage.setItem("deviceId", deviceId);
        }
        localStorage.setItem("accessToken", response.accessToken || "");
        localStorage.setItem("userData", JSON.stringify(savedUserData));
        localStorage.setItem("nin", JSON.stringify(userNin));
        if (Array.isArray(response?.notifications)) {
          localStorage.setItem(
            "notifications",
            JSON.stringify(response.notifications),
          );
        }

        setSnackbar({
          open: true,
          message: "Login Successful!",
          severity: "success",
        });

        setTimeout(() => {
          navigate("/home");
        }, 1500);
      } else {
        setSnackbar({
          open: true,
          message: response?.message || "Invalid email or password",
          severity: "error",
        });
      }
    } catch (error) {
      console.error(error);
      setSnackbar({
        open: true,
        message: "Network error. Please try again.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "89vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #f5f0ff 0%, #ffffff 100%)",
        py: 6,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: { xs: "90%", sm: 450 },
          p: { xs: 3, sm: 5 },
          borderRadius: "24px",
          background: "#fff",
          boxShadow: "0px 20px 60px rgba(82, 5, 123, 0.15)",
        }}
      >
        {/* Header Section */}
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <Box sx={{ mb: 2 }}>
            <img src={logo} alt="Dajoo" style={{ width: "120px" }} />
          </Box>
          <Typography
            variant="h5"
            sx={{ color: "#52057b", fontWeight: 800, mb: 1 }}
          >
            Welcome Back!
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: "#666", fontSize: "0.95rem" }}
          >
            Login to access your D'AJOO account
          </Typography>
        </Box>

        {/* Form */}
        <Box component="form">
          {/* Email */}
          <Typography sx={labelSx}>Email</Typography>
          <TextField
            placeholder="example@gmail.com"
            fullWidth
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            sx={inputSx}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Email sx={{ color: "#52057b", fontSize: "1.2rem" }} />
                </InputAdornment>
              ),
            }}
          />

          {/* Password */}
          <Typography sx={{ ...labelSx, mt: 3 }}>Password</Typography>
          <TextField
            placeholder="**********"
            fullWidth
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            sx={inputSx}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock sx={{ color: "#52057b", fontSize: "1.2rem" }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    sx={{ color: "#52057b" }}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {/* Login Button */}
          <Button
            fullWidth
            variant="contained"
            onClick={handleLogin}
            disabled={loading}
            sx={{
              mt: 4,
              bgcolor: "#52057b",
              color: "#fff",
              py: 2,
              borderRadius: "12px",
              fontWeight: "bold",
              fontSize: "1.1rem",
              textTransform: "none",
              boxShadow: "0px 10px 25px rgba(82, 5, 123, 0.3)",
              "&:hover": {
                bgcolor: "#3e055e",
                boxShadow: "0px 15px 35px rgba(82, 5, 123, 0.4)",
                transform: "translateY(-2px)",
              },
              "&.Mui-disabled": {
                bgcolor: "#e0e0e0",
                color: "#9e9e9e",
                boxShadow: "none",
              },
            }}
          >
            {loading ? (
              <CircularProgress size={24} sx={{ color: "#fff" }} />
            ) : (
              "Login"
            )}
          </Button>

          <Typography
            variant="body2"
            sx={{ textAlign: "center", mt: 3, color: "#666" }}
          >
            Don't have an account?{" "}
            <a
              href="/signup"
              style={{
                color: "#52057b",
                fontWeight: "bold",
                textDecoration: "none",
              }}
            >
              Sign Up
            </a>
          </Typography>
        </Box>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            severity={snackbar.severity}
            sx={{ width: "100%", borderRadius: "12px" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Paper>
    </Box>
  );
};

export default Login;
